import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';

const CART_ENTRY_POINT_SELECTOR = '[data-cart-entry-point]';
const CART_ANIMATION_DURATION_MS = 850;

@Injectable({
  providedIn: 'root',
})
export class CartAnimationService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private zone: NgZone
  ) {}

  animateToCart(source: EventTarget | null): void {
    const sourceElement = this.resolveElement(source);
    const targetElement = this.getVisibleCartEntryPoint();
    const win = this.document.defaultView;

    if (!win || !sourceElement || !targetElement) {
      return;
    }

    const sourceRect = sourceElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    if (!sourceRect.width || !sourceRect.height || !targetRect.width || !targetRect.height) {
      return;
    }

    this.zone.runOutsideAngular(() => this.runAnimation(sourceRect, targetRect, win));
  }

  private runAnimation(sourceRect: DOMRect, targetRect: DOMRect, win: Window): void {
    const flyer = this.createFlyer(win);
    const sourceCenter = this.getCenter(sourceRect);
    const targetCenter = this.getCenter(targetRect);
    const deltaX = targetCenter.x - sourceCenter.x;
    const deltaY = targetCenter.y - sourceCenter.y;
    const arcY = Math.min(-60, deltaY * 0.35);

    flyer.style.left = `${sourceCenter.x}px`;
    flyer.style.top = `${sourceCenter.y}px`;
    this.document.body.appendChild(flyer);

    const animation = flyer.animate(
      [
        { opacity: 1, transform: 'translate(-50%, -50%) translate(0, 0) scale(1)' },
        {
          opacity: 1,
          transform: `translate(-50%, -50%) translate(${deltaX * 0.45}px, ${deltaY * 0.45 + arcY}px) scale(1.15)`,
        },
        {
          opacity: 0.15,
          transform: `translate(-50%, -50%) translate(${deltaX}px, ${deltaY}px) scale(0.3)`,
        },
      ],
      {
        duration: CART_ANIMATION_DURATION_MS,
        easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
      }
    );

    animation.onfinish = () => {
      flyer.remove();
      this.pulseCartTarget();
    };
    animation.oncancel = () => flyer.remove();
  }

  private createFlyer(win: Window): HTMLDivElement {
    const flyer = this.document.createElement('div');
    const styles = win.getComputedStyle(this.document.documentElement);
    const primaryColor = styles.getPropertyValue('--button-primary').trim() || '#1d5eff';
    const backgroundColor = styles.getPropertyValue('--container-background').trim() || '#ffffff';

    flyer.textContent = 'shopping_cart';
    Object.assign(flyer.style, {
      alignItems: 'center',
      background: backgroundColor,
      border: `2px solid ${primaryColor}`,
      borderRadius: '50%',
      boxShadow: '0 0.35rem 1rem rgba(0, 0, 0, 0.28)',
      color: primaryColor,
      display: 'flex',
      fontFamily: 'Material Icons',
      fontSize: '1.35rem',
      height: '2.25rem',
      justifyContent: 'center',
      lineHeight: '1',
      pointerEvents: 'none',
      position: 'fixed',
      width: '2.25rem',
      zIndex: '10000',
    });

    return flyer;
  }

  private pulseCartTarget(): void {
    const target = this.getVisibleCartEntryPoint();
    target?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.16)' }, { transform: 'scale(1)' }],
      { duration: 260, easing: 'ease-out' }
    );
  }

  private getVisibleCartEntryPoint(): HTMLElement | null {
    const targets = Array.from(
      this.document.querySelectorAll<HTMLElement>(CART_ENTRY_POINT_SELECTOR)
    );

    return (
      targets.find((target) => {
        const rect = target.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      }) ?? null
    );
  }

  private getCenter(rect: DOMRect): { x: number; y: number } {
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }

  private resolveElement(source: EventTarget | null): HTMLElement | null {
    return source instanceof HTMLElement ? source : null;
  }
}
